// Utility functions for handling browser zoom level changes

/**
 * Get the current browser zoom level
 * @returns The current zoom level (1.0 = 100%, 1.25 = 125%, etc.)
 */
export function getZoomLevel(): number {
  return window.devicePixelRatio || 1;
}

/**
 * Get the zoom percentage (100 = 100%, 125 = 125%, etc.)
 * @returns The zoom percentage
 */
export function getZoomPercentage(): number {
  return Math.round(getZoomLevel() * 100);
}

/**
 * Check if the zoom level has changed significantly
 * @param oldZoom The previous zoom level
 * @param newZoom The current zoom level
 * @param threshold The threshold for considering a change significant (default: 0.01)
 * @returns True if the zoom level has changed significantly
 */
export function hasZoomChanged(oldZoom: number, newZoom: number, threshold: number = 0.01): boolean {
  return Math.abs(newZoom - oldZoom) > threshold;
}

/**
 * Debounced function to handle zoom level changes
 * @param callback The function to call when zoom changes
 * @param delay The delay in milliseconds (default: 100)
 * @returns A debounced function
 */
export function createZoomChangeHandler(callback: () => void, delay: number = 100): () => void {
  let timeoutId: NodeJS.Timeout;
  
  return () => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(callback, delay);
  };
}

/**
 * Force a chart resize by dispatching a custom event
 * This can be used to trigger chart re-initialization when zoom changes
 */
export function forceChartResize(): void {
  window.dispatchEvent(new CustomEvent('chartResize'));
}

/**
 * Add a zoom change listener that calls the provided callback
 * @param callback The function to call when zoom changes
 * @returns A cleanup function to remove the listener
 */
export function addZoomChangeListener(callback: () => void): () => void {
  let lastZoom = getZoomLevel();
  
  const handleZoomChange = () => {
    const currentZoom = getZoomLevel();
    if (hasZoomChanged(lastZoom, currentZoom)) {
      lastZoom = currentZoom;
      callback();
    }
  };

  // Listen for various events that might indicate zoom changes
  window.addEventListener('resize', handleZoomChange);
  window.addEventListener('orientationchange', handleZoomChange);
  
  // Use ResizeObserver as a fallback for more reliable detection
  const resizeObserver = new ResizeObserver(handleZoomChange);
  resizeObserver.observe(document.body);

  return () => {
    window.removeEventListener('resize', handleZoomChange);
    window.removeEventListener('orientationchange', handleZoomChange);
    resizeObserver.disconnect();
  };
}
